import {faEllipsisVertical} from "@fortawesome/pro-solid-svg-icons/faEllipsisVertical";
import {faFolderPlus} from "@fortawesome/pro-solid-svg-icons/faFolderPlus";
import {faGear} from "@fortawesome/pro-solid-svg-icons/faGear";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {CustomMailFolderType,} from '@twigsby/client-data';
import {
    Badge,
    Button,
    ButtonSize,
    ColourSolid,
    ContentBlock,
    ContentLoader,
    DropdownMenu, FilterInput,
    FlexAlign,
    FlexJustify,
    ItemMenuEntry,
    StackHorizontal,
    Tooltip,
    ToolTipPlacement, useComboBoxKeyboardNavigation, useFilterSelectOptions,
    useLayout,
    useTranslation
} from "@twigsby/frontend-framework";
import React, {useRef, useState} from 'react';
import {NavLink} from "react-router-dom";
import {useMailbox} from "../../Providers/NewMailboxProvider";
import {
    CustomFolderDragOver,
    CustomFolderEnter,
    CustomFolderLeave,
    DragCustomFolder,
    DropCustomFolder
} from "./Events/CustomFolderEvents";

type MailboxFoldersType = {
    mailItemContext?: boolean;
}

type SelectedFolderType = {
    id: number;
    name: string;
}

type MailFolderEntryType = {
    [key:string]: any;
    data: CustomMailFolderType;
    parentData?: CustomMailFolderType[];
    nested?: boolean;
    showCounts?: boolean;
    mailItemContext?: boolean;
    isFiltered?: boolean;
}

let dragIcon = document.createElement('img');
dragIcon.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
dragIcon.width = 0;
dragIcon.height = 0;

const MailboxFolders = (props: MailboxFoldersType) => {
    const { t } = useTranslation();
    const { isTouchInput } = useLayout();
    const { HandleFolderMove, ToggleCreateCustomFolderModal, customFolderData, customFolderStatus, customFolderError } = useMailbox();

    const [folderDragging, setFolderDragging] = useState<any>({ active: false, id: -1});

    const customFolderContainer = useRef<HTMLDivElement>(null);

    const {
        filterValue,
        filteredItems,
        FilterSelectList,
        ResetFilterSelectOptions,
    } = useFilterSelectOptions({ filterData: customFolderData as any, labelProp: 'description', valueProp: 'id' });

    const {
        HandleElementTransitionNavigation,
    } = useComboBoxKeyboardNavigation({
        itemContainerSelector: customFolderContainer.current,
        itemClassName: 'section-navigation-entry',
        skipClassName: 'multi-select-actions',
        resetCallback: ResetFilterSelectOptions,
    });

    const HandleDrop = (e: DragEvent) => {
        const result: any = DropCustomFolder(e);

        if(result.targetId && result.currentId) {
            HandleFolderMove(result);
        }

        setFolderDragging({ active: false, id: -1});
    }

    return (
        <div className={`custom-folder-container`}>
            {customFolderStatus === "loading" ? (
                <ContentLoader />
            ) : customFolderStatus === "error" ? (
                <ContentBlock error={true} title={customFolderError?.name as string} content={customFolderError?.message} />
            ) : (
                <>
                    {props.mailItemContext && (
                        <>
                            <div className={'p-xs'}>
                                <FilterInput
                                    filterValue={filterValue}
                                    hideArrow={true}
                                    resetCallback={() => {
                                        ResetFilterSelectOptions();
                                    }}
                                    active={true}
                                    onChange={(e) => FilterSelectList(e.currentTarget.value)}
                                    onKeyDown={(e) => HandleElementTransitionNavigation(e)}
                                    onBlur={(e) => null}
                                    onFocus={(e) => null}
                                    clearOnClose={true}
                                    autoFocusFilter={true}
                                />
                            </div>
                            <ItemMenuEntry title={t('divider')} sectionDivider={true} />
                            <ItemMenuEntry title={t('customFolders.actions.newFolder')} onClick={() => ToggleCreateCustomFolderModal(true)} icon={faFolderPlus} />
                            <ItemMenuEntry title={t('divider')} sectionDivider={true} />
                        </>
                    )}

                    {!props.mailItemContext && (
                        <StackHorizontal align={FlexAlign.Center} justify={FlexJustify.Between} extraClass={`section-navigation-title m-0`}>
                            <h6 className={'m-0 sub-heading'}>{t('customFolders.title')}</h6>

                            <Tooltip content={t('customFolders.actions.newFolder')} position={ToolTipPlacement.Right}>
                                <Button onClick={() => ToggleCreateCustomFolderModal(true)} title={t('customFolders.actions.newFolder')} squared={true} colour={ColourSolid.Primary} size={ButtonSize.Small} icon={faFolderPlus} iconOnly={true} />
                            </Tooltip>
                        </StackHorizontal>
                    )}

                    <div className={`custom-folder-container ${folderDragging.active ? 'dragging-active folder-dragging-active' : ''}`} ref={customFolderContainer}>
                        {filteredItems && filteredItems.map((folder, index: number) => (
                            <MailBoxPrimaryFolder
                                key={`customFolder${index}${folder.id}${props.mailItemContext ? 'MailItemContext' : 'MenuContext'}`}
                                data={folder}
                                parentData={filteredItems as Array<CustomMailFolderType>}
                                draggable={!isTouchInput}
                                isFiltered={!!filterValue}
                                mailItemContext={props.mailItemContext}
                                onDragStart={(e: any) => setFolderDragging(DragCustomFolder(e))}
                                onDragEnd={(e: any) => HandleDrop(e)}
                                onDragEnter={(e: any) => CustomFolderEnter(e)}
                                onDragLeave={(e: any) => CustomFolderLeave(e)}
                                onDragOver={(e: any) => CustomFolderDragOver(e)}
                            />
                        ))}

                        {!props.mailItemContext && (
                            <div
                                className={'section-navigation-entry folder-entry folder-reset'}
                                onDragEnd={(e: any) => HandleDrop(e)}
                                onDragEnter={(e: any) => CustomFolderEnter(e)}
                                onDragLeave={(e: any) => CustomFolderLeave(e)}
                                onDragOver={(e: any) => CustomFolderDragOver(e)}
                            />
                        )}
                    </div>

                    {!props.mailItemContext && filteredItems && filteredItems.length === 0 && (
                        <ContentBlock
                            stacked={true}
                            title={t('customFolders.noData.title')}
                            content={t('customFolders.noData.description')}
                            media={
                                <img src={`${process.env.PUBLIC_URL}/images/custom-folders.svg`} alt={t('customFolders.noData.imageAlt')} />
                            }
                            button={
                                <Button
                                    title={t('customFolders.actions.newFolder')}
                                    onClick={() => ToggleCreateCustomFolderModal(true)}
                                    colour={ColourSolid.Secondary}
                                />
                            }
                        />
                    )}

                    {props.mailItemContext && filteredItems && filteredItems.length === 0 && (
                        <div className={'flex-column align-center justify-center faded p-md'}>
                            <FontAwesomeIcon icon={faGear} fixedWidth={true} size={"2x"} />
                            <h6 className={'sm-sub-heading mt-sm mb-0'}>No Matching Folders</h6>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default MailboxFolders;

const MailBoxPrimaryFolder = (props: MailFolderEntryType) => {
    if(!props.isFiltered && props.data.parentFolderId && props.data.parentFolderId !== -1) return null;

    return (
        <>
            <CustomFolderEntry
                data={props.data}
                mailItemContext={props.mailItemContext}
                draggable={props.draggable}
                onDragStart={(e: DragEvent) => props.onDragStart(e)}
                onDragEnd={(e: DragEvent) => props.onDragEnd(e)}
                onDragEnter={(e: DragEvent) => props.onDragEnter(e)}
                onDragLeave={(e: DragEvent) => props.onDragLeave(e)}
                onDragOver={(e: DragEvent) => props.onDragOver(e)}
            />

            {props.parentData && (
                <>
                    {props.parentData.map((folder, index: number) => {
                        if(folder.parentFolderId !== props.data.id) return null;

                        return (
                            <CustomFolderEntry
                                key={`folder${index}`}
                                data={folder}
                                mailItemContext={props.mailItemContext}
                                draggable={props.draggable}
                                nested={true}
                                onDragStart={(e: DragEvent) => props.onDragStart(e)}
                                onDragEnd={(e: DragEvent) => props.onDragEnd(e)}
                                onDragEnter={(e: DragEvent) => props.onDragEnter(e)}
                                onDragLeave={(e: DragEvent) => props.onDragLeave(e)}
                                onDragOver={(e: DragEvent) => props.onDragOver(e)}
                            />
                        )
                    })}
                </>
            )}
        </>
    )
}

const CustomFolderEntry = (props: MailFolderEntryType) => {
    const { MoveItemsToFolder } = useMailbox();

    if(props.mailItemContext) {
        return (
            <div
                className={`section-navigation-entry folder-entry ${props.nested ? 'folder-entry-nested ml-sm' : ''}`}
                onClick={() => MoveItemsToFolder(props.data.id)}
            >
                <CustomFolderEntryContent {...props} />
            </div>
        )
    }

    return (
        <NavLink
            to={`../mail/custom-folder/${props.data.id}`}
            className={`section-navigation-entry folder-entry ${props.nested ? 'folder-entry-nested ml-sm' : ''}`}
            title={props.data.description}
            data-folder-id={props.data.id}
            draggable={props.draggable}
            onDragStart={props.onDragStart}
            onDragEnd={props.onDragEnd}
            onDragEnter={props.onDragEnter}
            onDragLeave={props.onDragLeave}
            onDragOver={props.onDragOver}
        >
            <CustomFolderEntryContent {...props} />
        </NavLink>
    )
}

const CustomFolderEntryContent = (props: MailFolderEntryType) => {
    const { t } = useTranslation();
    const { UpdateActiveFolderData, ToggleCreateCustomFolderModal, ToggleDeleteCustomFolderModal } = useMailbox();

    return (
     <>
         <Badge colour={props.data.colour} />

         <div className='section-navigation-entry-title ml-xs'>{props.data.description} {props.showCounts && props.data.count !== undefined && props.data.count > 0 && (<span className={'sm-sub-heading'}>({props.data.count})</span>)}</div>

         {!props.mailItemContext && (
             <>
                 {props.data.unreadCount !== undefined && props.data.unreadCount > 0 && (
                     <div className={'section-details ml-a'}>
                         <Badge value={props.data.unreadCount} colour={ColourSolid.Alert} />
                     </div>
                 )}

                 <DropdownMenu
                     trigger={
                         <div className={'section-actions folder-actions'}>
                             <div className={'section-action-button folder-actions-trigger'} title={t("customFolders.actions.options")} onClick={(e) => {
                                 e.preventDefault();
                                 UpdateActiveFolderData(props.data.id);
                             }}>
                                 <FontAwesomeIcon icon={faEllipsisVertical} fixedWidth={true} />
                             </div>
                         </div>
                     }
                 >
                     <ItemMenuEntry title={t('customFolders.actions.editFolder')} onClick={(e) => {
                         e.preventDefault();
                         ToggleCreateCustomFolderModal(true)
                     }} />
                     <ItemMenuEntry title={t('customFolders.actions.deleteFolder')} onClick={(e) => {
                         e.preventDefault();
                         ToggleDeleteCustomFolderModal(true)
                     }} />
                 </DropdownMenu>
             </>
         )}
     </>
    )
}
